import { supabase } from './supabaseClient';

export interface DashboardCategory {
  id: string;
  categoryKey: string;
  displayName: string;
  description?: string;
  icon?: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  isSystem: boolean;
  facilityId?: number;
  createdAt?: string;
  updatedAt?: string;
}

export class CategoryService {
  /**
   * Get all active categories for a facility
   */
  static async getCategories(facilityId?: string): Promise<DashboardCategory[]> {
    // Only log in development
    if (process.env.NODE_ENV !== 'production') {
      console.log('🔍 Fetching dashboard categories for facility:', facilityId || 'all');
    }
    
    let query = supabase
      .from('dashboard_categories')
      .select('*')
      .eq('is_active', true)
      .order('sort_order', { ascending: true });

    if (facilityId) {
      // Get only facility-specific categories
      query = query.eq('facility_id', parseInt(facilityId));
    }

    const { data, error } = await query;

    if (error) {
      console.error('❌ Error fetching categories:', error);
      throw error;
    }

    // Only log in development
    if (process.env.NODE_ENV !== 'production') {
      console.log(`✅ Retrieved ${data?.length || 0} categories for facility ${facilityId}`);
    }
    return (data || []).map(this.mapDbToCategory);
  }

  /**
   * Create a new category
   */
  static async createCategory(category: Omit<DashboardCategory, 'id' | 'createdAt' | 'updatedAt'>, facilityId: number): Promise<DashboardCategory> {
    console.log('🆕 Creating new category for facility:', facilityId, category);
    console.log('🔍 facilityId type:', typeof facilityId);
    console.log('🔍 facilityId value:', facilityId);
    console.log('🔍 facilityId is valid number:', !isNaN(facilityId) && facilityId > 0);
    
    const insertData = {
      category_key: category.categoryKey,
      display_name: category.displayName,
      description: category.description,
      icon: category.icon,
      color: category.color,
      sort_order: category.sortOrder,
      is_active: category.isActive,
      is_system: category.isSystem,
      facility_id: facilityId
    };
    
    console.log('📦 Insert data:', JSON.stringify(insertData, null, 2));
    
    const { data, error } = await supabase
      .from('dashboard_categories')
      .insert([insertData])
      .select()
      .single();

    if (error) {
      console.error('❌ Error creating category:', error);
      throw error;
    }

    console.log('✅ Category created successfully');
    return this.mapDbToCategory(data);
  }

  /**
   * Update an existing category
   */
  static async updateCategory(id: string, updates: Partial<Omit<DashboardCategory, 'id' | 'createdAt' | 'updatedAt'>>, facilityId?: number): Promise<DashboardCategory> {
    console.log('📝 Updating category:', id, updates);
    
    const updateData: any = {};
    if (updates.categoryKey) updateData.category_key = updates.categoryKey;
    if (updates.displayName) updateData.display_name = updates.displayName;
    if (updates.description !== undefined) updateData.description = updates.description;
    if (updates.icon !== undefined) updateData.icon = updates.icon;
    if (updates.color) updateData.color = updates.color;
    if (updates.sortOrder !== undefined) updateData.sort_order = updates.sortOrder;
    if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

    let query = supabase
      .from('dashboard_categories')
      .update(updateData)
      .eq('id', id);

    // Only allow updating categories for the current facility
    if (facilityId) {
      query = query.eq('facility_id', facilityId);
    }

    const { data, error } = await query.select().single();

    if (error) {
      console.error('❌ Error updating category:', error);
      throw error;
    }

    console.log('✅ Category updated successfully');
    return this.mapDbToCategory(data);
  }

  /**
   * Delete a category and all its associated metrics (cascade delete)
   */
  static async deleteCategory(id: string, facilityId?: number): Promise<void> {
    console.log('🗑️ Attempting to delete category:', id);
    
    // First check if it's a system category
    let query = supabase
      .from('dashboard_categories')
      .select('category_key, display_name, is_system, facility_id')
      .eq('id', id);

    if (facilityId) {
      query = query.eq('facility_id', facilityId);
    }

    const { data: category, error: fetchError } = await query.single();

    if (fetchError) {
      console.error('❌ Error fetching category for deletion:', fetchError);
      throw fetchError;
    }

    if (category.is_system) {
      throw new Error(`Cannot delete system category: ${category.display_name}`);
    }

    // Delete all metrics in this category first
    console.log('🗑️ Deleting metrics in category:', category.category_key);
    const { error: metricsDeleteError } = await supabase
      .from('metric_configs')
      .delete()
      .eq('tab', category.category_key);

    if (metricsDeleteError) {
      console.error('❌ Error deleting metrics:', metricsDeleteError);
      throw new Error(`Failed to delete metrics in category: ${metricsDeleteError.message}`);
    }

    // Delete dimensions associated with this category
    console.log('🗑️ Clearing dimension associations for category:', id);
    const { error: dimensionsUpdateError } = await supabase
      .from('wellness_dimensions')
      .update({ category_id: null })
      .eq('category_id', id);

    if (dimensionsUpdateError) {
      console.error('❌ Error clearing dimension associations:', dimensionsUpdateError);
      // Continue anyway - this is not critical
    }

    // Finally delete the category itself
    let deleteQuery = supabase
      .from('dashboard_categories')
      .delete()
      .eq('id', id);

    if (facilityId) {
      deleteQuery = deleteQuery.eq('facility_id', facilityId);
    }

    const { error: deleteError } = await deleteQuery;

    if (deleteError) {
      console.error('❌ Error deleting category:', deleteError);
      throw deleteError;
    }

    console.log('✅ Category and all associated metrics deleted successfully');
  }

  /**
   * Get metrics count for each category
   */
  static async getCategoryMetricCounts(): Promise<Record<string, number>> {
    const { data, error } = await supabase
      .from('metric_configs')
      .select('tab')
      .eq('is_visible', true);

    if (error) {
      console.error('❌ Error fetching metric counts:', error);
      return {};
    }

    const counts: Record<string, number> = {};
    data?.forEach(row => {
      const tab = row.tab || 'other';
      counts[tab] = (counts[tab] || 0) + 1;
    });

    return counts;
  }

  /**
   * Get next available sort order
   */
  static async getNextSortOrder(facilityId?: number): Promise<number> {
    let query = supabase
      .from('dashboard_categories')
      .select('sort_order')
      .order('sort_order', { ascending: false })
      .limit(1);

    if (facilityId) {
      query = query.eq('facility_id', facilityId);
    }

    const { data, error } = await query;

    if (error || !data || data.length === 0) {
      return 10; // Default starting sort order
    }

    return (data[0].sort_order || 0) + 10;
  }

  /**
   * Validate category key format
   */
  static validateCategoryKey(key: string): string | null {
    if (!key) return 'Category key is required';
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      return 'Category key must start with lowercase letter and contain only lowercase letters, numbers, and underscores';
    }
    if (key.length < 2) return 'Category key must be at least 2 characters';
    if (key.length > 50) return 'Category key must be 50 characters or less';
    return null;
  }

  /**
   * Map database row to DashboardCategory
   */
  private static mapDbToCategory(row: any): DashboardCategory {
    return {
      id: row.id || '',
      categoryKey: row.category_key || '',
      displayName: row.display_name || '',
      description: row.description || '',
      icon: row.icon || '',
      color: row.color || '#667eea',
      sortOrder: row.sort_order || 0,
      isActive: row.is_active !== false,
      isSystem: row.is_system === true,
      facilityId: row.facility_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at
    };
  }
}
